/**
 * What a numeric field does with what the trader types.
 *
 * Three moments, three rules:
 *  - typing: keep it out of the way. Strip what cannot belong in a number, cap
 *    the decimals, and otherwise leave the text exactly as typed — reformatting
 *    mid-entry moves the caret and fights the trader. What WOULD change the
 *    meaning if stripped (an exponent, a minus, a second separator) rejects the
 *    keystroke instead: "1e3" is not 13, "-1" is not 1, "1.2.3" is not 1.23.
 *  - pasting: accept what a human would copy ("1,234.56", "1.234,56", "5e-7")
 *    and normalise it once.
 *  - blur / submit: resolve onto the field's grid (`NumericFieldSpec.finalize`).
 */
import { dec } from "./decimal";
import { NumericFieldSpec } from "./spec";

/**
 * Characters whose removal would leave a DIFFERENT number rather than a cleaner
 * one: "1e3" stripped is 13, "-1" stripped is 1. Letters, spaces and currency
 * signs are noise around a number; these are part of what it means.
 */
const MEANINGFUL_NON_DIGIT = /[eE-]/;

/**
 * True when stripping `text` down to digits and one separator would change its
 * meaning: an exponent, a sign, or a second decimal separator ("1.2.3", "1,2.3").
 */
export const typingChangesMeaning = (raw: string): boolean => {
  const text = String(raw ?? "");
  if (MEANINGFUL_NON_DIGIT.test(text)) return true;
  return (text.match(/[.,]/g) ?? []).length > 1;
};

/**
 * Digits, at most one separator, nothing else. Never reformats.
 *
 * `previous` is what the field held before this keystroke. When the new text
 * carries an exponent, a minus or a second separator, the keystroke is
 * REJECTED and `previous` comes back unchanged (2026-10-01 QA B-6: typed "1e3"
 * became 13, "-1" became 1, "1.2.3" became 1.23 — a different order, silently).
 * Without `previous` the offending characters are dropped as before, which is
 * the same thing for one keystroke at the end of the text but not for text
 * that arrives whole (autofill, drag-and-drop, a caret in the middle).
 */
export const sanitizeTyping = (
  raw: string,
  maxDecimals: number,
  previous?: string
): string => {
  if (previous !== undefined && typingChangesMeaning(raw)) return previous;
  const text = String(raw ?? "").replace(",", ".");
  // Keep the first dot, drop later ones; drop every non-digit.
  let seenDot = false;
  let kept = "";
  for (const char of text) {
    if (char >= "0" && char <= "9") kept += char;
    else if (char === "." && !seenDot && maxDecimals > 0) {
      seenDot = true;
      kept += char;
    }
  }
  if (kept === ".") return "0.";
  const [whole, fraction] = kept.split(".");
  // "007" -> "7", but "0" and "0." survive as typed.
  const trimmedWhole = whole.replace(/^0+(?=\d)/, "");
  if (fraction === undefined) return trimmedWhole;
  return `${trimmedWhole || "0"}.${fraction.slice(0, maxDecimals)}`;
};

/**
 * Pasted text as a plain decimal, or `null` when it is not a number a trader
 * could have meant. Handles both thousands conventions by treating the LAST
 * separator as the decimal point.
 */
export const normalizePaste = (raw: string, maxDecimals: number): string | null => {
  const text = String(raw ?? "")
    .trim()
    .replace(/[\s_'$€£]/g, "");
  if (text === "") return null;

  const lastDot = text.lastIndexOf(".");
  const lastComma = text.lastIndexOf(",");
  let candidate = text;
  if (lastDot >= 0 && lastComma >= 0) {
    // The later separator is the decimal point; the other groups thousands.
    candidate =
      lastComma > lastDot
        ? text.replace(/\./g, "").replace(",", ".")
        : text.replace(/,/g, "");
  } else if (lastComma >= 0) {
    // A lone comma: a decimal point ("1,5") unless it groups ("1,234").
    const after = text.length - lastComma - 1;
    candidate = after === 3 ? text.replace(/,/g, "") : text.replace(",", ".");
  }

  const parsed = dec.parse(candidate);
  if (parsed === null || dec.isNeg(parsed)) return null;
  return dec.fixed(parsed, maxDecimals, "floor").replace(/\.?0+$/, "") || "0";
};

/**
 * The value a field holds once the trader leaves it: resolved onto the grid, at
 * the field's own precision. An empty field stays empty — it used to become "0".
 */
export const finalizeValue = (raw: string, spec: NumericFieldSpec): string => {
  const text = String(raw ?? "").trim();
  if (text === "") return "";
  const parsed = dec.parse(text);
  if (parsed === null) return "";
  if (spec.finalize === "none") return dec.fixed(parsed, spec.maxDecimals, "floor");

  const snapped = dec.toStep(parsed, spec.step, spec.finalize);
  const bounded =
    snapped === null
      ? parsed
      : spec.max !== undefined && dec.gt(snapped, spec.max)
      ? dec.toStep(spec.max, spec.step, "floor") ?? spec.max
      : snapped;
  return dec.fixed(bounded, spec.maxDecimals, spec.finalize);
};
